import React, { useEffect, useRef, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendEmailVerification,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebase-config';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const betaInvite = searchParams.get('beta') === '1';
  const promoCode  = searchParams.get('promo') || null; // set when arriving from pricing page
  const returnTo   = searchParams.get('returnTo') || null;
  const safeReturnTo = returnTo && returnTo.startsWith('/') ? returnTo : null;
  const signedInRedirectPath = '/v2-course';
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirm: '', betaCode: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // Prevents the already-logged-in redirect from firing mid-signup before Firestore writes complete
  const isSigningUpRef = useRef(false);
  // betaValid is only set after server-side code validation — never derived from a
  // client-side comparison so no secret ends up in the compiled JS bundle.
  const [betaValid, setBetaValid] = useState(betaInvite); // auto-true when arriving via invite link

  function handleClose() {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(returnTo || '/');
  }

  useEffect(() => {
    function onKeydown(event) {
      if (event.key === 'Escape') {
        handleClose();
      }
    }
    function onWheel(event) {
      // Prevent background scroll when cursor is on modal overlay but not on scrollable content
      event.preventDefault();
    }
    window.addEventListener('keydown', onKeydown);
    window.addEventListener('wheel', onWheel, { passive: false });
    
    // Block body scroll while modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('wheel', onWheel);
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    if (isSigningUpRef.current) return; // suppress during active signup
    navigate(signedInRedirectPath, { replace: true });
  }, [currentUser, navigate, signedInRedirectPath]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Reset betaValid if the user changes the code field so they must re-submit
    if (name === 'betaCode') setBetaValid(false);
  };

  async function resolvePromoCode() {
    let resolvedPromoCode = promoCode || null;

    if (!betaInvite && formData.betaCode.trim()) {
      try {
        const fns = getFunctions();
        const validatePromoCode = httpsCallable(fns, 'validatePromoCode');
        const { data: codeResult } = await validatePromoCode({ code: formData.betaCode.trim() });
        if (!codeResult.valid) {
          setError('That beta code is not valid or has already been used.');
          return null;
        }
        resolvedPromoCode = formData.betaCode.trim().toUpperCase();
        setBetaValid(true);
      } catch {
        setError('Could not verify beta code — please try again.');
        return null;
      }
    }

    return resolvedPromoCode;
  }

  async function persistUserProfile(user, displayName, resolvedPromoCode) {
    const userDocRef = doc(db, 'users', user.uid);
    const existingDoc = await getDoc(userDocRef);
    const isBeta = betaValid || betaInvite || !!resolvedPromoCode;

    if (!existingDoc.exists()) {
      await setDoc(userDocRef, {
        name: displayName,
        displayName,
        email: user.email?.trim().toLowerCase() || '',
        tier: null,
        ...(isBeta ? { betaTester: true, pendingBetaCode: resolvedPromoCode || '' } : {}),
        createdAt: serverTimestamp(),
      });
    } else if (isBeta) {
      await setDoc(userDocRef, {
        betaTester: true,
        pendingBetaCode: resolvedPromoCode || '',
      }, { merge: true });
    }

    if (resolvedPromoCode) {
      try {
        const fns = getFunctions();
        const redeemPromoCode = httpsCallable(fns, 'redeemPromoCode');
        await redeemPromoCode({ code: resolvedPromoCode });
      } catch {
        console.warn('Beta code storage failed — betaTester flag still applied.');
      }
    }

    return isBeta;
  }

  async function handleGoogleSignup() {
    setError('');
    setInfo('');
    setGoogleLoading(true);

    try {
      const resolvedPromoCode = await resolvePromoCode();
      if (formData.betaCode.trim() && !resolvedPromoCode) {
        setGoogleLoading(false);
        return;
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      // Must be set BEFORE signInWithPopup — onAuthStateChanged fires before the
      // promise resolves, so setting it after is too late and causes immediate signout.
      isSigningUpRef.current = true;
      sessionStorage.setItem('om_session_active', '1');
      const { user } = await signInWithPopup(auth, provider);
      const displayName = user.displayName?.trim() || user.email?.split('@')[0] || 'Operator Method User';
      const isBeta = await persistUserProfile(user, displayName, resolvedPromoCode);

      navigate(isBeta ? '/pricing' : (returnTo || '/v2-course'));
    } catch (err) {
      isSigningUpRef.current = false;
      const msgs = {
        'auth/account-exists-with-different-credential': 'An account already exists with this email using another sign-in method.',
        'auth/popup-closed-by-user': 'Google sign-in was closed before completion.',
        'auth/cancelled-popup-request': 'Google sign-in was canceled.',
      };
      setError(msgs[err.code] || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!formData.email || !formData.password) {
      setError('Email and password are required.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (formData.password !== formData.confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const resolvedPromoCode = await resolvePromoCode();
      if (formData.betaCode.trim() && !resolvedPromoCode) {
        setLoading(false);
        return;
      }

      // Must be set BEFORE createUserWithEmailAndPassword — onAuthStateChanged fires
      // before the promise resolves, so setting it after is too late.
      isSigningUpRef.current = true;
      sessionStorage.setItem('om_session_active', '1');
      const { user } = await createUserWithEmailAndPassword(
        auth, formData.email.trim(), formData.password
      );
      if (formData.name.trim()) {
        await updateProfile(user, { displayName: formData.name.trim() });
      }
      const isBeta = await persistUserProfile(user, formData.name.trim() || '', resolvedPromoCode);
      await sendEmailVerification(user);
      await signOut(auth);
      const loginParams = new URLSearchParams();
      loginParams.set('verifyEmail', '1');
      loginParams.set('email', formData.email.trim().toLowerCase());
      if (returnTo) loginParams.set('returnTo', returnTo);
      if (isBeta) loginParams.set('postVerify', 'pricing');
      navigate(`/login?${loginParams.toString()}`);
    } catch (err) {
      const msgs = {
        'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password must be at least 8 characters.',
      };
      setError(msgs[err.code] || 'Something went wrong. Please try again.');
      if (err.code === 'auth/email-already-in-use') {
        setInfo('Use Sign in to continue to your course.');
      }
    } finally {
      setLoading(false);
    }
  }

  const inputStyle = {
    width: '100%',
    padding: '7px 10px',
    backgroundColor: '#141c2e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    color: '#f0f4ff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    color: '#8892a4',
    marginBottom: '3px',
    letterSpacing: '0.08em',
  };

  const fieldWrapStyle = {
    width: '100%',
    maxWidth: '34ch',
    margin: '0 auto',
  };

  const contentWrapStyle = {
    width: '100%',
    maxWidth: '36ch',
    margin: '0 auto',
  };

  const pageStyle = {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(5, 10, 22, 0.75)',
    backdropFilter: 'blur(4px)',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: '16px',
    zIndex: 1200,
    overflowY: 'hidden',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  };

  const shellStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: '430px',
    height: 'auto',
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  };

  const cardStyle = {
    width: '100%',
    background: 'linear-gradient(180deg, rgba(14, 21, 39, 0.96), rgba(8, 13, 28, 0.98))',
    borderRadius: '16px',
    border: '1px solid rgba(122, 160, 228, 0.24)',
    padding: '24px 18px 18px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(79, 142, 247, 0.1), inset 0 1px 0 rgba(255,255,255,0.06)',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  const backPlateStyle = {
    position: 'absolute',
    inset: '10px 0 0 10px',
    borderRadius: '16px',
    background: 'linear-gradient(160deg, rgba(19, 32, 59, 0.4), rgba(8, 14, 26, 0.25))',
    border: '1px solid rgba(79, 142, 247, 0.08)',
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    pointerEvents: 'none',
  };

  const glowStyle = {
    position: 'absolute',
    inset: '-5% 10% auto 10%',
    height: '100px',
    background: 'radial-gradient(circle, rgba(79,142,247,0.12) 0%, rgba(79,142,247,0.04) 40%, transparent 70%)',
    pointerEvents: 'none',
    filter: 'blur(4px)',
  };

  const brandLabel = {
    fontSize: '10px',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#4f8ef7',
    marginBottom: '8px',
    fontWeight: 600,
    textAlign: 'center',
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(107, 122, 153, 0.22)',
    borderRadius: '999px',
    background: 'rgba(10, 16, 30, 0.82)',
    color: '#8ea0bf',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: 1,
    zIndex: 2,
    padding: 0,
    boxSizing: 'border-box',
  };

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .signup-modal-float {
          animation: float 3.5s ease-in-out infinite;
        }
      `}</style>
      <div style={pageStyle} onClick={handleClose}>
        <div style={shellStyle} className="signup-modal-float" onClick={(e) => e.stopPropagation()}>
        <div style={backPlateStyle} aria-hidden="true" />
        <div style={glowStyle} aria-hidden="true" />
        <div style={cardStyle}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '0',
            background:
              'linear-gradient(90deg, transparent 0%, rgba(79,142,247,0.06) 24%, transparent 25%, transparent 74%, rgba(208,171,98,0.05) 75%, transparent 100%), repeating-linear-gradient(180deg, transparent 0, transparent 28px, rgba(255,255,255,0.025) 29px, transparent 30px)',
            pointerEvents: 'none',
            opacity: 0.9,
          }}
        />
        <button
          type="button"
          onClick={handleClose}
          style={closeButtonStyle}
          aria-label="Close create account modal"
        >
          ×
        </button>
        <div style={brandLabel}>The Operator Method</div>
        {currentUser ? (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#f0f4ff', margin: '0 0 4px 0' }}>
              Redirecting to your course...
            </h2>
          </>
        ) : (
          <>
        <div style={contentWrapStyle}>
          <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#f0f4ff', margin: '0 0 1px 0' }}>
          Create your account
        </h2>
        <p style={{ color: '#6b7a99', margin: '0 0 8px 0', fontSize: '12px', lineHeight: 1.4 }}>
          {betaInvite
            ? 'Beta invitation verified — create your account, then select your enrollment on the pricing page at no cost.'
            : 'Free to sign up — or enter a beta code for full access.'}
        </p>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignup}
          disabled={googleLoading || loading}
          style={{
            ...fieldWrapStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '8px',
            borderRadius: '7px',
            border: 'none',
            backgroundColor: '#1f2937',
            color: '#f0f4ff',
            fontSize: '13px',
            fontWeight: 600,
            cursor: googleLoading || loading ? 'not-allowed' : 'pointer',
            marginBottom: '8px',
            boxShadow: '0 2px 4px rgba(66, 133, 244, 0.3)',
          }}
        >
          <span style={{ fontSize: '14px', lineHeight: 1, color: '#4285f4', fontWeight: 700 }}>G</span>
          {googleLoading ? 'Connecting Google...' : 'Continue with Google'}
        </button>

        <div style={{
          ...contentWrapStyle,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '8px',
          color: '#4a5568',
          fontSize: '10px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          <span style={{ flex: 1, height: '1px', backgroundColor: '#1e2a3a' }} />
          <span>or create with email</span>
          <span style={{ flex: 1, height: '1px', backgroundColor: '#1e2a3a' }} />
        </div>

        {error && (
          <div style={{
            ...contentWrapStyle,
            padding: '8px 10px',
            backgroundColor: '#1a0f0f',
            border: '1px solid #5c1f1f',
            borderRadius: '7px',
            color: '#f87171',
            fontSize: '11px',
            marginBottom: '8px',
          }}>
            {error}
          </div>
        )}

        {info && (
          <div style={{
            ...contentWrapStyle,
            padding: '8px 10px',
            backgroundColor: 'rgba(79,142,247,0.08)',
            border: '1px solid rgba(79,142,247,0.2)',
            borderRadius: '7px',
            color: '#9fc3ff',
            fontSize: '11px',
            marginBottom: '8px',
          }}>
            {info}
            {error.includes('already exists') && (
              <div style={{ marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const loginPath = safeReturnTo
                      ? `/login?returnTo=${encodeURIComponent(safeReturnTo)}`
                      : '/login';
                    navigate(loginPath, { state: { backgroundLocation: location } });
                  }}
                  style={{
                    padding: '7px 10px',
                    backgroundColor: 'rgba(79,142,247,0.18)',
                    color: '#cfe2ff',
                    border: '1px solid rgba(79,142,247,0.45)',
                    borderRadius: '7px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Go to Sign In
                </button>
              </div>
            )}
          </div>
        )}

        {betaInvite && (
          <div style={{
            ...contentWrapStyle,
            padding: '8px 10px',
            backgroundColor: 'rgba(52,211,153,0.08)',
            border: '1px solid rgba(52,211,153,0.3)',
            borderRadius: '7px',
            color: '#34d399',
            fontSize: '10px',
            fontWeight: 600,
            lineHeight: 1.4,
            marginBottom: '8px',
          }}>
            ✓ Beta invitation verified — select any tier at no cost after creating your account
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>NAME</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange}
              placeholder="Jane Smith" style={inputStyle} />
          </div>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>EMAIL</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              required placeholder="you@example.com" style={inputStyle} />
          </div>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>PASSWORD</label>
            <input type="password" name="password" value={formData.password} onChange={handleChange}
              required placeholder="Min. 8 characters" style={inputStyle} />
          </div>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>CONFIRM PASSWORD</label>
            <input type="password" name="confirm" value={formData.confirm} onChange={handleChange}
              required placeholder="Repeat password" style={inputStyle} />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              ...fieldWrapStyle,
              marginTop: '1px', padding: '8px',
              backgroundColor: loading ? '#1e2a3a' : '#4f8ef7',
              color: loading ? '#4a5568' : '#fff',
              border: 'none', borderRadius: '7px',
              fontSize: '13px', fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>

          <button
            type="button"
            onClick={handleClose}
            style={{
              ...fieldWrapStyle,
              marginTop: '1px',
              padding: '7px',
              backgroundColor: 'transparent',
              color: '#8da4c4',
              border: '1px solid rgba(141,164,196,0.35)',
              borderRadius: '7px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Not now
          </button>
        </form>

        <p style={{ ...contentWrapStyle, marginTop: '8px', textAlign: 'center', color: '#4a5568', fontSize: '10px' }}>
          Already have an account?{' '}
          <Link to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'} style={{ color: '#4f8ef7', textDecoration: 'none' }}>Sign in</Link>
        </p>
          </>
        )}
        </div>
      </div>
    </div>
    </>
  );
}
